import { Injectable, signal } from '@angular/core';
import { environment } from '@env/environment';
import { ChatService } from '@features/chat/chat.service';
import { Case } from '@models/case-open.interface';
import { SupplyDrop, SupplyDropInformation, SupplyDropState } from '@models/supply-drop.interface';
import { SocketService } from '@shared/socket.service';
import { sleep } from '@utils';

@Injectable({
  providedIn: 'root',
})
export class SupplyDropService {
  supplyDrop = signal<SupplyDropState>({
    status: 'none',
    supplyDropInformation: null,
    supplyDrop: null,
    captcha: null,
    players: 0,
  });

  private rolling = false;

  constructor(private socketService: SocketService, private chatService: ChatService) {
    this.registerDevTestHelpers();

    // `update_captcha` and `start_drop` both ride the socket's connect burst; this service is
    // eagerly created at bootstrap (see provideAppInitializer in main.ts) so both listeners
    // exist before that burst lands. Note the BE sends a live `timestamp` but a frozen,
    // long-past `end_time` on `start_drop` (measured 2026-08-19) — never filter on it.
    this.socketService.socket.on('update_captcha', (data: { captcha: string }) => {
      this.supplyDrop.update((prev) => ({
        ...prev,
        captcha: data?.captcha ?? null,
      }));
    });

    this.socketService.socket.on('start_drop', async (data: SupplyDropInformation) => {
      this.applyDropStart(data);

      await sleep(500);
      this.chatService.addSupplyDropStartMessage(this.informationToCase(data), data?.message ?? '');
    });

    this.socketService.socket.on('update_drop_players', (data: { players: number }) => {
      this.supplyDrop.update((prev) => ({
        ...prev,
        players: data?.players ?? 0,
      }));
    });

    this.socketService.socket.on('end_drop', (data: SupplyDrop) => {
      this.supplyDrop.update((prev) => ({
        ...prev,
        supplyDrop: data,
        status: 'rolling',
      }));
    });
  }

  private applyDropStart(data: SupplyDropInformation) {
    this.supplyDrop.update((prev) => ({
      ...prev,
      supplyDropInformation: data,
      status: 'running',
      players: 0,
    }));
  }

  /**
   * Claims the single in-flight roll for the current drop, returning false if one is already
   * running. Lives here rather than on the widget because the widget is destroyed and recreated
   * whenever chat is collapsed: a fresh widget would otherwise see its own flag as false while the
   * detached sequence from the previous instance is still mid-roll, and post the end card twice.
   */
  tryClaimRoll(): boolean {
    if (this.rolling) {
      return false;
    }
    this.rolling = true;
    return true;
  }

  releaseRoll() {
    this.rolling = false;
  }

  /** Called by the widget once the roll sequence (avatars + skin reel) has finished. */
  postEndMessage(data: SupplyDrop) {
    this.chatService.addSupplyDropEndMessage(data);
  }

  /**
   * Returns the widget to its idle state. Kept on the service (not the widget) so a widget that
   * gets torn down mid-roll — e.g. chat collapsed — can't leave state stuck on 'rolling'.
   */
  resetToNone() {
    this.supplyDrop.update((prev) => ({
      status: 'none',
      captcha: prev.captcha,
      supplyDropInformation: null,
      supplyDrop: null,
      players: 0,
    }));
  }

  async joinSupplyDrop(captcha: string): Promise<string> {
    return this.socketService.emit<string>('join_drop', { captcha });
  }

  /** `start_drop` only pushes flat skin fields; the chat start-card renders it like a mini `Case`. */
  private informationToCase(data: SupplyDropInformation): Case {
    const skinName = data?.skin_name ?? '';

    return {
      name: skinName,
      link: skinName.toLowerCase().replace(/\s+/g, '-'),
      image: data?.image ?? '',
      price: data?.price ?? 0,
      tier: data?.type,
    };
  }

  /**
   * Ported from tf2-hunt (`supply-drop.service.ts:28,40`) so P6.2(j) can verify the widget/chat
   * cards render without waiting on a live drop. Dev-mode only — never registered in production.
   */
  private registerDevTestHelpers() {
    if (environment.production) {
      return;
    }

    (window as any).__testSupplyDropStart = () => {
      const testInformation: SupplyDropInformation = {
        timestamp: Math.floor(Date.now() / 1000),
        type: 3,
        message: '',
        skin_name: 'Bronze V Case',
        price: 0,
        // UNIX seconds, matching the real payload — two minutes of join window.
        end_time: Math.floor(Date.now() / 1000) + 120,
        image: 'assets/images/cases/3_bronze/bronze_5.png',
      };

      this.supplyDrop.update((prev) => ({
        ...prev,
        supplyDropInformation: testInformation,
        status: 'running',
        players: 0,
      }));

      this.chatService.addSupplyDropStartMessage(this.informationToCase(testInformation), testInformation.message);
    };

    (window as any).__testSupplyDropEnd = () => {
      const testDrop: SupplyDrop = {
        winner: {
          steamid: '123',
          name: 'TestPlayer',
          avatar: 'https://steamcdn-a.akamaihd.net/steamcommunity/public/images/avatars/fe/fef49e7fa7e1997310d705b2a6158ff8dc1cdfeb_full.jpg',
        },
        winneravatar: 'https://steamcdn-a.akamaihd.net/steamcommunity/public/images/avatars/fe/fef49e7fa7e1997310d705b2a6158ff8dc1cdfeb_full.jpg',
        winnername: 'TestPlayer',
        timestamp: 0,
        message: '',
        avatars: ['a', 'b', 'c', 'd', 'e'],
        winnerid: 0,
        result: [{ casename: 'Bronze V Case', case: 'bronze-v', skin: 'Test Skin', price: 1.5 }],
        case: {
          name: 'Bronze V Case',
          link: 'bronze-v',
          image: 'assets/images/cases/3_bronze/bronze_5.png',
          price: 0,
          tier: 3,
          category: 'bronze',
          skins: [{ name: 'Test Skin', image: 'assets/images/cases/3_bronze/bronze_5.png', color: '#cd7f32', price: 1.5 }],
        },
        gameid: 'test-game-123',
        ticket: 42,
        ticketPercentage: 15.3,
      };

      this.supplyDrop.update((prev) => ({
        ...prev,
        supplyDrop: testDrop,
        status: 'rolling',
      }));
    };
  }
}
